import { useState, useRef, useCallback } from "react";
import { TextField, Box } from "@mui/material";
import PropTypes from "prop-types";
import { useOtp } from "context/OtpContext";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { OTP_LOGIN_ACTION } from "constants/authConstants";
import TurnstileWidget from "components/TurnstileWidget";
import config from "config/config";
import SignInWithGoogle from "./SignInWithGoogle";

const envConfig = config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"];

const IdentifierInput = ({
  gtmCode,
  setAuthSection,
  campaignCode,
  campaignDetails,
  storeId,
  brandId,
  isSignup,
  label,
}) => {
  const { identifier, isLoading, handleSendOtp, dispatch } = useOtp();
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    dispatch({ type: OTP_LOGIN_ACTION.SET_IDENTIFIER, payload: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendOtp(captchaToken, resetCaptcha);
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="identifier"
        label={label}
        name="identifier"
        autoComplete="identifier"
        FormHelperTextProps={{
          sx: { color: "#475467" },
        }}
        value={identifier}
        onChange={handleChange}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#a9a9a9",
            },
            "&:hover fieldset": {
              borderColor: "#a9a9a9",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#808080",
            },
          },
        }}
      />

      <TurnstileWidget
        ref={turnstileRef}
        siteKey={envConfig.TURNSTILE_SITE_KEY}
        onToken={setCaptchaToken}
        onExpire={() => setCaptchaToken(null)}
        onError={() => setCaptchaToken(null)}
      />

      <MDButton
        type="submit"
        variant="contained"
        color="success"
        sx={{
          width: "100%",
          marginTop: "1rem",
          textTransform: "none",
          borderRadius: "8px",
          fontWeight: "500",
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
        disabled={isLoading || !identifier || !captchaToken}
        fullWidth
      >
        Continue
      </MDButton>

      <MDBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
          mt: 2.5,
          mb: 2,
        }}
      >
        <MDBox
          sx={{
            height: "1px",
            flex: 1,
            background: "#AFAFAF",
          }}
        />
        <MDTypography
          variant="button"
          sx={{
            color: "#AFAFAF",
            fontSize: "0.875rem",
            fontWeight: 500,
          }}
        >
          or
        </MDTypography>
        <MDBox
          sx={{
            height: "1px",
            flex: 1,
            background: "#AFAFAF",
          }}
        />
      </MDBox>

      {!isSignup && (
        <MDButton
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={() => setAuthSection("password")}
          sx={{
            borderRadius: "8px",
            py: 1.5,
            background: "#E8E8E8",
            borderColor: "#E8E8E8",
            marginTop: "1rem",
            color: "#475467",
            textTransform: "none",
            fontWeight: "500",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            "&:hover": {
              background: "#D1D1D1",
              borderColor: "#D1D5DB",
              color: "#101828",
            },
          }}
        >
          Login with password
        </MDButton>
      )}
      <SignInWithGoogle
        gtmCode={gtmCode}
        campaignCode={campaignCode}
        campaignDetails={campaignDetails}
        storeId={storeId}
        brandId={brandId}
      />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        style={{ marginTop: "24px" }}
      >
        {!isSignup ? (
          <span style={{ fontSize: 12, maxWidth: "360px", color: "#6B6B6B", lineHeight: "20px" }}>
            By proceeding, you consent to get calls, SMS messages & emails from Catering Rewards and
            its affiliates to the number provided. Reply “STOP” to opt out. Message & Data rates may
            apply.
          </span>
        ) : (
          <span
            style={{
              fontSize: 12,
              maxWidth: "360px",
              color: "#6B6B6B",
              lineHeight: "20px",
              marginBottom: "10px",
            }}
          >
            By proceeding, you consent to get calls, SMS messages & emails from Catering Rewards and
            its affiliates to the number provided. Reply “STOP” to opt out. Message & Data rates may
            apply.
          </span>
        )}
      </Box>
    </form>
  );
};

export default IdentifierInput;

IdentifierInput.propTypes = {
  setAuthSection: PropTypes.func,
  gtmCode: PropTypes.string,
  campaignCode: PropTypes.string,
  campaignDetails: PropTypes.object,
  storeId: PropTypes.string,
  brandId: PropTypes.string,
  isSignup: PropTypes.bool,
  label: PropTypes.string,
};
